
import { Vue, Component } from 'vue-property-decorator';
import {
  getTeamDetail, getMyTeams, updateTeam, uploadTeamPhoto,
} from '@/api/team';

@Component
export default class TeamProfileEdit extends Vue {
  private teamUid = ''

  private teamName = ''

  private teamNameInput = ''

  private teamCode = ''

  private logoPreview = ''

  private logoFileUid = ''

  private newLogoFile: File | null = null

  private bankName = ''

  private bankAccount = ''

  private selectedDays: string[] = []

  private selectedTimes: string[] = []

  private selectedGender = ''

  private selectedAges: string[] = []

  private homeStadium = ''

  private selectedCity = ''

  private selectedDistrict = ''

  private createdDate = ''

  private isSaving = false

  private activeDayOptions = [
    { label: '일', value: 'sunday' },
    { label: '월', value: 'monday' },
    { label: '화', value: 'tuesday' },
    { label: '수', value: 'wednesday' },
    { label: '목', value: 'thursday' },
    { label: '금', value: 'friday' },
    { label: '토', value: 'saturday' },
  ]

  private activeTimeOptions = [
    { label: '6시-10시', value: '아침' },
    { label: '10시-18시', value: '낮' },
    { label: '18시-24시', value: '저녁' },
    { label: '24시-6시', value: '심야' },
  ]

  private genderOptions = [
    { label: '남성', value: 'male' },
    { label: '여성', value: 'female' },
    { label: '남녀 모두', value: 'mixed' },
  ]

  private ageRangeOptions = [
    { label: '10대', value: '10s' },
    { label: '20대', value: '20s' },
    { label: '30대', value: '30s' },
    { label: '40대', value: '40s' },
    { label: '50대', value: '50s' },
    { label: '60대', value: '60s' },
  ]

  private cities = [
    { label: '서울', value: '서울' },
    { label: '경기', value: '경기' },
    { label: '인천', value: '인천' },
    { label: '부산', value: '부산' },
    { label: '대구', value: '대구' },
    { label: '대전', value: '대전' },
    { label: '광주', value: '광주' },
    { label: '울산', value: '울산' },
    { label: '세종', value: '세종' },
    { label: '경남', value: '경남' },
    { label: '경북', value: '경북' },
    { label: '전남', value: '전남' },
    { label: '전북', value: '전북' },
    { label: '충남', value: '충남' },
    { label: '충북', value: '충북' },
    { label: '강원', value: '강원' },
    { label: '제주', value: '제주' },
  ]

  private districtsByCity: Record<string, Array<{ label: string, value: string }>> = {
    경남: [
      { label: '진주시', value: '진주시' },
      { label: '창원시', value: '창원시' },
      { label: '김해시', value: '김해시' },
      { label: '양산시', value: '양산시' },
      { label: '거제시', value: '거제시' },
      { label: '통영시', value: '통영시' },
      { label: '사천시', value: '사천시' },
      { label: '밀양시', value: '밀양시' },
    ],
    서울: [
      { label: '강남구', value: '강남구' },
      { label: '서초구', value: '서초구' },
      { label: '송파구', value: '송파구' },
      { label: '강동구', value: '강동구' },
      { label: '마포구', value: '마포구' },
      { label: '영등포구', value: '영등포구' },
    ],
    경기: [
      { label: '수원시', value: '수원시' },
      { label: '성남시', value: '성남시' },
      { label: '고양시', value: '고양시' },
      { label: '용인시', value: '용인시' },
      { label: '부천시', value: '부천시' },
      { label: '안산시', value: '안산시' },
    ],
    부산: [
      { label: '해운대구', value: '해운대구' },
      { label: '부산진구', value: '부산진구' },
      { label: '동래구', value: '동래구' },
      { label: '남구', value: '남구' },
      { label: '사하구', value: '사하구' },
    ],
    대구: [
      { label: '수성구', value: '수성구' },
      { label: '달서구', value: '달서구' },
      { label: '북구', value: '북구' },
    ],
    인천: [
      { label: '남동구', value: '남동구' },
      { label: '부평구', value: '부평구' },
      { label: '서구', value: '서구' },
    ],
  }

  // Day bitmask mapping
  private dayBitmask: Record<string, number> = {
    sunday: 1,
    monday: 2,
    tuesday: 4,
    wednesday: 8,
    thursday: 16,
    friday: 32,
    saturday: 64,
  }

  // Time bitmask mapping
  private timeBitmask: Record<string, number> = {
    아침: 1,
    낮: 2,
    저녁: 4,
    심야: 8,
  }

  // Age bitmask mapping
  private ageBitmask: Record<string, number> = {
    '10s': 1,
    '20s': 2,
    '30s': 4,
    '40s': 8,
    '50s': 16,
    '60s': 32,
  }

  get districts() {
    return this.selectedCity
      ? this.districtsByCity[this.selectedCity] || []
      : [];
  }

  get canEditName(): boolean {
    if (!this.createdDate) return false;
    const diffMs = Date.now() - new Date(this.createdDate).getTime();
    return diffMs <= 7 * 24 * 60 * 60 * 1000;
  }

  async created(): Promise<void> {
    this.teamUid = (this.$route.query.teamUid as string) || '';
    if (!this.teamUid) {
      try {
        const res = await getMyTeams();
        const team = Array.isArray(res.data) ? res.data[0] : res.data;
        if (team && team.uid) {
          this.teamUid = team.uid;
        }
      } catch (e) {
        console.warn('내 팀 조회 실패:', e);
      }
    }
    if (this.teamUid) {
      await this.loadTeamData();
    }
  }

  private async loadTeamData(): Promise<void> {
    try {
      const res = await getTeamDetail(this.teamUid);
      const team = res.data || {};

      this.teamName = team.name || '';
      this.teamNameInput = team.name || '';
      this.teamCode = team.teamCode || '';
      this.createdDate = team.createdDate || '';
      this.logoPreview = team.logoUrl || '';
      this.logoFileUid = team.logoFileUid || '';
      this.bankName = team.bankName || '';
      this.bankAccount = team.bankAccount || '';
      this.homeStadium = team.homeStadium || '';
      this.selectedCity = team.regionSido || '';
      this.selectedDistrict = team.regionSigungu || '';

      // Decode bitmask → selected arrays
      if (team.activeDays) {
        this.selectedDays = this.decodeBitmask(team.activeDays, this.dayBitmask);
      }
      if (team.activeTimeSlots) {
        this.selectedTimes = this.decodeBitmask(team.activeTimeSlots, this.timeBitmask);
      }
      if (team.genderType !== undefined && team.genderType !== null) {
        const genderMap: Record<number, string> = {
          0: 'male',
          1: 'female',
          2: 'mixed',
        };
        this.selectedGender = genderMap[team.genderType] || '';
      }
      if (team.ageGroups) {
        this.selectedAges = this.decodeBitmask(team.ageGroups, this.ageBitmask);
      }
    } catch (error) {
      console.error('팀 정보 로드 실패:', error);
      this.$message.error('팀 정보를 불러오는데 실패했습니다.');
    }
  }

  private decodeBitmask(value: number, mapping: Record<string, number>): string[] {
    const result: string[] = [];
    Object.entries(mapping).forEach(([key, bit]) => {
      // eslint-disable-next-line no-bitwise
      if (value & bit) {
        result.push(key);
      }
    });
    return result;
  }

  private encodeBitmask(selected: string[], mapping: Record<string, number>): number {
    let result = 0;
    selected.forEach((key) => {
      if (mapping[key]) {
        // eslint-disable-next-line no-bitwise
        result |= mapping[key];
      }
    });
    return result;
  }

  private toggleOption(array: string[], value: string): void {
    const index = array.indexOf(value);
    if (index > -1) {
      array.splice(index, 1);
    } else {
      array.push(value);
    }
  }

  private triggerLogoUpload(): void {
    (this.$refs.logoInput as HTMLInputElement).click();
  }

  private handleLogoChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    this.newLogoFile = input.files[0];
    const reader = new FileReader();
    reader.onload = (e) => {
      this.logoPreview = (e.target?.result as string) || '';
    };
    reader.readAsDataURL(this.newLogoFile);
  }

  private onCityChange(): void {
    this.selectedDistrict = '';
  }

  private goBack(): void {
    this.$router.go(-1);
  }

  private async handleSave(): Promise<void> {
    this.isSaving = true;
    try {
      // 새 로고가 있으면 업로드
      if (this.newLogoFile) {
        const uploadRes = await uploadTeamPhoto(this.newLogoFile);
        if (uploadRes.data && uploadRes.data.uid) {
          this.logoFileUid = uploadRes.data.uid;
        }
      }

      const genderMap: Record<string, number> = {
        male: 0,
        female: 1,
        mixed: 2,
      };

      await updateTeam({
        uid: this.teamUid,
        name: this.canEditName ? this.teamNameInput : undefined,
        logoFileUid: this.logoFileUid || undefined,
        bankName: this.bankName || undefined,
        bankAccount: this.bankAccount || undefined,
        activeDays: this.encodeBitmask(this.selectedDays, this.dayBitmask) || undefined,
        activeTimeSlots: this.encodeBitmask(this.selectedTimes, this.timeBitmask) || undefined,
        genderType: this.selectedGender ? genderMap[this.selectedGender] : undefined,
        ageGroups: this.encodeBitmask(this.selectedAges, this.ageBitmask) || undefined,
        homeStadium: this.homeStadium || undefined,
        regionSido: this.selectedCity || undefined,
        regionSigungu: this.selectedDistrict || undefined,
      });

      this.$message.success('팀 프로필이 수정되었습니다.');
      this.$router.go(-1);
    } catch (error) {
      console.error('팀 프로필 수정 실패:', error);
      this.$message.error('팀 프로필 수정에 실패했습니다.');
    } finally {
      this.isSaving = false;
    }
  }
}
